import { createComposerUploadScheduler } from './composer-upload-scheduler';
import {
  composerUploadFailureCause,
  composerUploadFailureReason,
  type ComposerUploadFailureCause,
  type ComposerUploadFailureReason,
} from '@/lib/uploads/composer-upload-route';
import type {
  ComposerUploadProgress,
  ComposerUploadProgressStore,
} from '@/lib/uploads/composer-upload-progress';
import { reportUserBlockingFailure } from '@/lib/observability/user-blocking-failure-reporter';
import type { OrgChatAttachmentInput } from './org-chat-types';
import type { ReferenceSelectionCategory } from './reference-selection-policy';
import type {
  CompletedComposerUpload,
  RecordedComposerUpload,
} from './composer-input/shared';

export type ComposerUploadFailure = {
  uploadId: string;
  filename: string;
  requiresReselect?: boolean;
  /** Set when a retry would fail the same way, so only Remove is offered. */
  reason?: Exclude<ComposerUploadFailureReason, 'retryable'>;
  /** For a retryable failure: what went wrong, when it's known. */
  cause?: ComposerUploadFailureCause;
};
/** An upload still running whose bytes stopped moving; Retry restarts it. */
export type ComposerUploadStall = { uploadId: string; filename: string };
/**
 * The new owner of an upload a send took with it (#4844). It outlives the
 * composer: dispose and reconcile leave it running, and its outcome goes
 * here instead of into the composer's state.
 */
export type ComposerUploadHandoff = {
  publish: (attachment: OrgChatAttachmentInput | null) => void;
  failed: (failure: Omit<ComposerUploadFailure, 'filename'>) => void;
};
/**
 * Gives a handed-off upload back to the composer as it now stands (still
 * running, failed, or stored), for a send that never came to exist.
 */
export type ComposerUploadReclaim = () => void;
type UploadEntry = {
  file: File;
  attachment: OrgChatAttachmentInput & { uploadId: string };
  category?: ReferenceSelectionCategory;
  publish: (attachment: OrgChatAttachmentInput | null) => void;
  controller: AbortController;
  state: 'uploading' | 'failed' | 'ready';
  failure?: ComposerUploadFailureReason;
  cause?: ComposerUploadFailureCause;
  completed?: CompletedComposerUpload;
  /** What `publish` was given once it was stored and registered. */
  published?: OrgChatAttachmentInput;
  running: boolean;
  /** Retry pressed while a stalled attempt was still running. */
  restartPending?: boolean;
  handoff?: ComposerUploadHandoff;
};
type UploadServices = {
  upload: (
    entry: Pick<UploadEntry, 'file' | 'attachment' | 'category'>,
    signal: AbortSignal,
    onProgress: (loadedBytes: number, totalBytes: number) => void
  ) => Promise<CompletedComposerUpload>;
  register: (
    upload: CompletedComposerUpload,
    category?: ReferenceSelectionCategory
  ) => Promise<RecordedComposerUpload>;
  changed: (snapshot: {
    processing: boolean;
    failures: ComposerUploadFailure[];
    stalls: ComposerUploadStall[];
  }) => void;
  /** Live byte progress for the chips (#4809). */
  progress?: ComposerUploadProgressStore;
  releasePreview: (url: string) => void;
  /** Called once per failed attempt (analytics); never throws into the lifecycle. */
  failed?: (
    error: unknown,
    entry: Pick<UploadEntry, 'file' | 'attachment' | 'completed'>,
    /** The bytes the upload had sent, read before the progress was cleared. */
    progress: ComposerUploadProgress | null
  ) => void;
};

/** Owns each local file through upload, authorized registration, retry and removal. */
export function createComposerUploadLifecycle(services: UploadServices) {
  const entries = new Map<string, UploadEntry>();
  const scheduler = createComposerUploadScheduler();
  let disposed = false;
  // Entries a send took with it are reported to their new owner instead.
  const own = () => [...entries.values()].filter((entry) => !entry.handoff);
  const live = (entry: UploadEntry) => !disposed || Boolean(entry.handoff);
  const notify = () => {
    if (disposed) return;
    services.changed({
      processing: own().some((entry) => entry.state === 'uploading'),
      failures: own()
        .filter((entry) => entry.state === 'failed')
        .map((entry) => ({
          uploadId: entry.attachment.uploadId,
          filename: entry.attachment.filename,
          ...(entry.failure && entry.failure !== 'retryable'
            ? { reason: entry.failure }
            : entry.cause
              ? { cause: entry.cause }
              : {}),
        })),
      stalls: own()
        .filter(
          (entry) =>
            entry.state === 'uploading' &&
            services.progress?.get(entry.attachment.uploadId)?.stalled
        )
        .map((entry) => ({
          uploadId: entry.attachment.uploadId,
          filename: entry.attachment.filename,
        })),
    });
  };
  const release = (entry: UploadEntry) => {
    entry.controller.abort();
    services.progress?.clear(entry.attachment.uploadId);
    if (entry.attachment.previewUrl?.startsWith('blob:'))
      services.releasePreview(entry.attachment.previewUrl);
  };

  async function run(uploadId: string) {
    const entry = entries.get(uploadId);
    if (!entry || entry.running || entry.state === 'ready' || !live(entry))
      return;
    // Each attempt owns its controller: a restart replaces the entry's, so
    // the stalled attempt sees its own abort and stops touching the entry.
    const { controller } = entry;
    const active = () =>
      live(entry) &&
      entries.get(uploadId) === entry &&
      !controller.signal.aborted;
    entry.running = true;
    entry.state = 'uploading';
    entry.failure = undefined;
    entry.cause = undefined;
    notify();
    try {
      await scheduler.run(async () => {
        if (!active()) return;
        entry.completed ??= await services.upload(
          entry,
          controller.signal,
          (loadedBytes, totalBytes) => {
            if (!active()) return;
            // Every byte sent: the server is now verifying and filing it.
            if (totalBytes > 0 && loadedBytes >= totalBytes)
              services.progress?.finishing(uploadId);
            else services.progress?.sent(uploadId, loadedBytes, totalBytes);
          }
        );
        if (!active()) return;
        services.progress?.finishing(uploadId);
        const registered = await services.register(
          entry.completed,
          entry.category
        );
        if (!active()) return;
        entry.state = 'ready';
        entry.published = {
          ...entry.attachment,
          storageKey: entry.completed.storageKey,
          ...(entry.completed.url ? { url: entry.completed.url } : {}),
          workspaceFileId: registered.fileId,
          vfsPath: registered.path,
          isUploading: false,
        };
        services.progress?.clear(uploadId);
        if (entry.handoff) entries.delete(uploadId);
        entry.publish(entry.published);
      }, controller.signal);
    } catch (error) {
      // Keep the original File and any completed object key for an explicit retry.
      // A registration retry never repeats a successfully completed object upload.
      if (active()) {
        entry.state = 'failed';
        entry.failure = composerUploadFailureReason(error);
        entry.cause = composerUploadFailureCause(error);
        const progress = services.progress?.get(uploadId) ?? null;
        services.progress?.clear(uploadId);
        try {
          // Too large, a full drive, a dropped or cut-off connection and a
          // file the browser cannot read are the user's to fix; any other
          // failure is ours to alert on (#6012). `file_upload_failed` still
          // records every one of them with its stage and class (#7485).
          if (
            entry.failure === 'retryable' &&
            entry.cause !== 'network' &&
            entry.cause !== 'interrupted' &&
            entry.cause !== 'unreadable'
          )
            reportUserBlockingFailure({
              surface: 'upload',
              operation: 'composer_upload',
              error,
            });
          services.failed?.(error, entry, progress);
          entry.handoff?.failed({
            uploadId,
            ...(entry.failure !== 'retryable'
              ? { reason: entry.failure }
              : entry.cause
                ? { cause: entry.cause }
                : {}),
          });
        } catch {
          // Reporting must never change the upload's state.
        }
      }
    } finally {
      entry.running = false;
      if (entry.restartPending && entries.get(uploadId) === entry) {
        entry.restartPending = false;
        void run(uploadId);
      } else notify();
    }
  }
  /** Retry: runs a failed upload again, or restarts a stalled one. */
  function retry(uploadId: string) {
    const entry = entries.get(uploadId);
    if (!entry || entry.state === 'ready' || !live(entry))
      return Promise.resolve();
    if (!entry.running) return run(uploadId);
    entry.restartPending = true;
    entry.controller.abort();
    entry.controller = new AbortController();
    services.progress?.clear(uploadId);
    notify();
    return Promise.resolve();
  }
  function reclaim(
    uploadId: string,
    entry: UploadEntry,
    handoff: ComposerUploadHandoff,
    ownerPublish: UploadEntry['publish']
  ) {
    if (entry.handoff !== handoff) return;
    entry.handoff = undefined;
    entry.publish = ownerPublish;
    const current = entries.get(uploadId);
    // Removed meanwhile: nothing comes back. One stored while away left the
    // map; it returns with its stored copy.
    if (current ? current !== entry : entry.state !== 'ready') return;
    if (disposed) {
      // The composer is gone too: nobody owns it any more.
      if (current) entries.delete(uploadId);
      release(entry);
      return;
    }
    if (!current) {
      entries.set(uploadId, entry);
      if (entry.published) entry.publish(entry.published);
    }
    notify();
  }
  return {
    has: (uploadId: string) => entries.has(uploadId),
    /**
     * Read-only view of one upload: what a send needs to hand it off
     * (#4844), whether it is still running here, and once it is stored,
     * the stored copy the composer was given.
     */
    entryFor(uploadId: string) {
      const entry = entries.get(uploadId);
      if (!entry) return undefined;
      return {
        file: entry.file,
        attachment: entry.attachment,
        category: entry.category,
        state: entry.state,
        handedOff: Boolean(entry.handoff),
        // A send reads this when the composer has not rendered it yet.
        published: entry.published,
      };
    },
    activate() {
      disposed = false;
    },
    add(
      input: Pick<UploadEntry, 'file' | 'attachment' | 'category' | 'publish'>
    ) {
      if (disposed || entries.has(input.attachment.uploadId)) return;
      entries.set(input.attachment.uploadId, {
        ...input,
        controller: new AbortController(),
        state: 'uploading',
        running: false,
      });
      notify();
    },
    run,
    retry,
    /**
     * Give a running or failed upload to a new owner that outlives this
     * composer. Returns how to give it back, or null when there is nothing
     * (left) to hand off.
     */
    handOff(
      uploadId: string,
      handoff: ComposerUploadHandoff
    ): ComposerUploadReclaim | null {
      const entry = entries.get(uploadId);
      if (!entry || entry.state === 'ready' || entry.handoff) return null;
      const ownerPublish = entry.publish;
      entry.handoff = handoff;
      entry.publish = handoff.publish;
      notify();
      return () => reclaim(uploadId, entry, handoff, ownerPublish);
    },
    /** Marks uploads with no new bytes for a while as stalled. */
    sweepStalls() {
      const stalled = services.progress?.sweepStalls() ?? [];
      if (stalled.some((uploadId) => entries.has(uploadId))) notify();
    },
    remove(uploadId: string) {
      const entry = entries.get(uploadId);
      if (!entry) return;
      entries.delete(uploadId);
      release(entry);
      if (live(entry)) entry.publish(null);
      notify();
    },
    reconcile(selectedIds: Set<string>) {
      for (const [id, entry] of entries) {
        if (!selectedIds.has(id) && !entry.handoff) {
          entries.delete(id);
          release(entry);
        }
      }
      notify();
    },
    dispose() {
      disposed = true;
      for (const [id, entry] of entries) {
        if (entry.handoff) continue;
        entries.delete(id);
        release(entry);
      }
    },
  };
}
export type ComposerUploadLifecycle = ReturnType<
  typeof createComposerUploadLifecycle
>;
